import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { TEMPERATURE } from "../components/LeadCard";
import type { CrmCatalog, LeadView, Manager } from "../types";

/**
 * Воронка: колонки по статусам, карточки перетаскиваются мышкой.
 *
 * На телефоне перетаскивать пальцем неудобно и браузеры это толком не
 * поддерживают, поэтому там у каждой карточки есть выпадающий список
 * «куда перенести».
 */
export function PipelinePage() {
  const [catalog, setCatalog] = useState<CrmCatalog | null>(null);
  const [managers, setManagers] = useState<Manager[]>([]);
  const [leads, setLeads] = useState<LeadView[]>([]);
  const [events, setEvents] = useState<string[]>([]);
  const [owner, setOwner] = useState("");
  const [event, setEvent] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dragId, setDragId] = useState<number | null>(null);
  const [overCol, setOverCol] = useState<string | null>(null);

  useEffect(() => {
    api.leadCatalog().then(setCatalog).catch(() => setCatalog(null));
    api.managers().then(setManagers).catch(() => setManagers([]));
  }, []);

  const load = useCallback(() => {
    api
      .listLeads({
        owner_id: owner || undefined,
        event: event || undefined,
        search: search || undefined,
      })
      .then((r) => {
        setLeads(r.leads);
        setEvents(r.stats.events);
        setError("");
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [owner, event, search]);

  useEffect(() => {
    const t = setTimeout(load, search ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  /** Перенос сразу на экране; если сервер откажет — возвращаем как было. */
  const move = async (lead: LeadView, status: string) => {
    if (lead.status === status) return;
    const before = leads;
    setLeads((list) => list.map((l) => (l.meeting_id === lead.meeting_id ? { ...l, status } : l)));
    try {
      await api.updateLead(lead.meeting_id, { status });
      setError("");
    } catch (e) {
      setLeads(before);
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const statuses = catalog?.statuses ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Воронка</h1>
          <p>
            Где сейчас каждый клиент. Перетащите карточку в другую колонку —
            статус поменяется, а в истории клиента появится отметка.
          </p>
        </div>
      </div>

      <div className="filter-row">
        <input
          placeholder="Поиск по компании или контакту"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="filter-search"
        />
        <select value={owner} onChange={(e) => setOwner(e.target.value)}>
          <option value="">Все менеджеры</option>
          {managers.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
        {events.length > 0 && (
          <select value={event} onChange={(e) => setEvent(e.target.value)}>
            <option value="">Все выставки</option>
            {events.map((ev) => (
              <option key={ev} value={ev}>
                {ev}
              </option>
            ))}
          </select>
        )}
      </div>

      {error && <div className="banner red">{error}</div>}

      {loading && leads.length === 0 ? (
        <div className="card">
          <div className="empty">
            <span className="spinner" /> Загружаю…
          </div>
        </div>
      ) : (
        <div className="board">
          {statuses.map((col) => {
            const cards = leads.filter((l) => l.status === col.key);
            return (
              <section
                key={col.key}
                className={`board-col${overCol === col.key ? " over" : ""}`}
                onDragOver={(e) => {
                  if (dragId === null) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  if (overCol !== col.key) setOverCol(col.key);
                }}
                onDragLeave={(e) => {
                  // Уходим из колонки, а не с одной карточки на другую внутри неё
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) setOverCol(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const lead = leads.find((l) => l.meeting_id === dragId);
                  setOverCol(null);
                  setDragId(null);
                  if (lead) void move(lead, col.key);
                }}
              >
                <header className="board-head">
                  <span>{col.label}</span>
                  <span className="board-count">{cards.length}</span>
                </header>

                <div className="board-cards">
                  {cards.length === 0 && <div className="board-empty">Пусто</div>}
                  {cards.map((l) => {
                    const temp = TEMPERATURE[l.temperature];
                    const title =
                      [l.company, l.contact_name].filter(Boolean).join(" — ") || "без названия";
                    return (
                      <article
                        key={l.meeting_id}
                        className={`board-card${dragId === l.meeting_id ? " dragging" : ""}`}
                        draggable
                        onDragStart={(e) => {
                          setDragId(l.meeting_id);
                          e.dataTransfer.effectAllowed = "move";
                          e.dataTransfer.setData("text/plain", String(l.meeting_id));
                        }}
                        onDragEnd={() => {
                          setDragId(null);
                          setOverCol(null);
                        }}
                      >
                        <Link to={`/meetings/${l.meeting_id}`} className="board-title">
                          {title}
                        </Link>

                        <div className="row" style={{ gap: 4, marginTop: 6 }}>
                          <span className={`badge ${temp.cls}`}>{temp.label}</span>
                          {l.category && <span className="badge">{l.category}</span>}
                          {l.stale && <span className="badge red">тишина {l.silent_days} дн.</span>}
                        </div>

                        <div className="muted small" style={{ marginTop: 6 }}>
                          {[l.owner_name, l.city].filter(Boolean).join(" · ") || "менеджер не назначен"}
                        </div>

                        {/* На тач-экранах перетаскивания нет — переносим списком */}
                        <select
                          className="board-move"
                          value={l.status}
                          onChange={(e) => void move(l, e.target.value)}
                          aria-label="Перенести в колонку"
                        >
                          {statuses.map((s) => (
                            <option key={s.key} value={s.key}>
                              {s.label}
                            </option>
                          ))}
                        </select>
                      </article>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </>
  );
}
